import { ReactElement } from 'react';
import { Controller, Control, FieldPath, FieldValues } from 'react-hook-form';
import { EmailChipInput } from 'components/common/ChipInput/EmailChipInput';
import { FieldWrapper, FieldWrapperPassThroughProps } from './FieldWrapper';

type EmailChipInputFieldProps<T extends FieldValues> = FieldWrapperPassThroughProps & {
  name: FieldPath<T>;
  control: Control<T>;
  placeholder?: string;
  /** Maximum height before scrolling (in pixels or chakra units) */
  maxHeight?: string | number;
  /** Whether to allow duplicate emails */
  allowDuplicates?: boolean;
  width?: string | number;
  minWidth?: string | number;
  /** Custom error message for invalid emails */
  invalidEmailMessage?: string;
};

/**
 * EmailChipInputField component - A form field wrapper for EmailChipInput using react-hook-form Controller
 * Easy to use with react-hook-form forms, handles validation and error states automatically
 *
 * @example
 * <EmailChipInputField
 *   name="emails"
 *   control={control}
 *   placeholder="Add email addresses..."
 * />
 */
export const EmailChipInputField = <T extends FieldValues>({
  name,
  control,
  label,
  error,
  placeholder = 'Add email addresses...',
  maxHeight = '200px',
  allowDuplicates = false,
  width,
  minWidth,
  labelPosition,
  invalidEmailMessage,
}: EmailChipInputFieldProps<T>): ReactElement => {
  return (
    <FieldWrapper label={label} error={error} labelPosition={labelPosition}>
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, value } }) => (
          <EmailChipInput
            placeholder={placeholder}
            values={value || []}
            onChange={onChange}
            maxHeight={maxHeight}
            allowDuplicates={allowDuplicates}
            width={width}
            minWidth={minWidth}
            invalidEmailMessage={invalidEmailMessage}
          />
        )}
      />
    </FieldWrapper>
  );
};
